<template>
  <Input
    v-model:value="barcode"
    v-bind="$attrs"
    :class="['mts-scan-bar', { active: !!barcode }]"
    :placeholder="placeholder"
    :readOnly="hasScaner"
    allowClear
    @focus="() => (hasScaner = getScaner())"
    @press-enter="onSearch(barcode, 'input')"
  >
    <template #prefix>
      <MIcon
        prefix="g"
        icon="scan"
        :size="20"
        class="leading-none text-[#01A887]"
        @click.stop="openCamera"
      />
    </template>
    <template #suffix>
      <Divider vertical class="!m-0 !h-full !border-[#E5E7EB]" />
      <LoadingOutlined v-if="loading" spin class="px-4px" />
      <MIcon
        v-else
        prefix="g"
        icon="search"
        :size="20"
        class="px-4px mr-[-4px] leading-none text-[#6F7680]"
        @click.stop="() => onSearch(barcode, 'input')"
      />
    </template>
  </Input>
</template>

<script setup lang="ts">
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import { Input } from 'ant-design-vue';
  import { computed, nextTick, onActivated, ref, shallowReactive } from 'vue';
  import { useRoute } from 'vue-router';
  import { Divider } from '@mts-fe/components';
  import { useComponentStatus } from '@mts-fe/hooks';
  import { $MTS } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { mtsEmitter } from '@mts/utils';
  import { getGlobalStore } from '@/store';

  const props = defineProps<{
    loading?: boolean;
    /** 冻结扫码功能 */
    freeze?: boolean;
    /** 扫码后自动失去焦点 */
    autoBlur?: boolean;
    placeholder?: string;
    /** 自动移除前缀0 */
    trimPrefixZero?: boolean;
  }>();
  type BarcodeFrom = 'scan' | 'input' | 'camera';
  const emit = defineEmits<{
    (event: 'change', res: { barcode: string; from: BarcodeFrom }): void;
  }>();

  const placeholder = computed(() => {
    if (props.placeholder) return t(props.placeholder);
    return (hasScaner.value ? t('m.25090382') : t('common.inputText')) + t('m.25090383');
  });

  /** 获取当前是否连接了扫码枪 */
  const getScaner = () => $MTS.App.prop('sys.prop.scanDev') !== null;
  const hasScaner = ref(getScaner());

  const barcode = defineModel<string>('value', { default: '' });
  const onSearch = (value: string, from: BarcodeFrom) => {
    barcode.value = props.trimPrefixZero ? value.replace(/^0+/, '') : value;
    props.autoBlur && (document.activeElement as HTMLElement)?.blur?.();
    nextTick().then(() => emit('change', { barcode: barcode.value, from }));
  };

  const { isActive } = useComponentStatus();
  mtsEmitter.on('scannerReadCode', (data) => {
    isActive.value && !props.freeze && onSearch(data.code, 'scan');
  });

  const enableCamera = getGlobalStore().getConfig['pad:scan_enable_camera'];
  const openCamera = () =>
    enableCamera && $MTS.App.getQRCode({ cb: ({ data }) => data && onSearch(data, 'camera') });
</script>

<script lang="ts">
  export const useBarcode = (loader?: (params: Recordable) => Promise<void>) => {
    const handleSearch = async ({ barcode, ...rest } = { barcode: barState.barcode }) => {
      if (!barcode) return;

      barState.loading = true;
      barState.barcode = barcode;
      loader?.({ barcode, ...rest }).finally(() => (barState.loading = false));
    };

    const { query } = useRoute();
    const barState = shallowReactive({
      barcode: query.barcode as string,
      loading: false,
    });

    onActivated(() => {
      barState.barcode = query.barcode as string;
    });

    return { barState, handleSearch };
  };
</script>

<style lang="less">
  .mts-scan-bar.ant-input-affix-wrapper {
    flex-shrink: 0;
    width: 185px;
    border-radius: 20px;
    border-color: #ebedf0;
    background-color: #ebedf0;

    &.active,
    &-focused {
      width: 240px;
    }

    .ant-input {
      background-color: inherit;
    }
  }
</style>
